import { getDvaApp } from 'umi';
import { Auth } from '@/aws-config';
import User from '@/services/user';
import Workspace from '@/services/workspace';
import Dataset from '@/services/dataset';
import { graphql } from './graphql';
import { assertIsString } from './typeChecks';

async function checkAllowedSuperAdminUser(): Promise<void> {
  const userId = (await Auth.currentAuthenticatedUser()).getUsername();
  if (
    userId !== '3ce2df7a-cd74-48d4-a366-23d03d4bfaca' &&
    userId !== 'auth0_auth0|6706f3026fa73bd80d34e01e' &&
    userId !== 'auth0_auth0|6708030447da86e649292f08'
  ) {
    throw new Error('checkAllowedSuperAdminUser failed');
  }
}

export async function __graphql(command: any, variables: any, rnt = false): Promise<unknown> {
  await checkAllowedSuperAdminUser();
  return graphql(command, variables, rnt);
}

export async function refreshAllDatasetsInGivenFolder(folderId: string) {
  const datasets = (await Dataset.getDatasetsByFolder(folderId)).filter(
    (el) => !el.triggerDetails.locked,
  );
  return await Promise.all(
    datasets.map(async (dataset) => {
      return await Dataset.refresh(dataset.cloudResourceId);
    }),
  );
}

export async function updateCronExpressionForAllDatasetsWithNamePrefix(
  namePrefix: string,
  hoursToAdd: number,
  minutesToAdd: number,
  confirmed = false,
) {
  const reduxState = getDvaApp()._store.getState().organization;
  const { currentWorkspace } = reduxState;
  assertIsString(currentWorkspace, 'BEEM231103210252');
  let datasets = await Dataset.getDatasetsOwnByWorkspace(currentWorkspace);
  datasets = datasets.filter((el) => el.name.toLowerCase().startsWith(namePrefix.toLowerCase()));
  for (let i = 0; i < datasets.length; i += 1) {
    const { id, name, trigger, triggerDetails } = datasets[i];
    if (trigger === 'CRON') {
      const currentCron = triggerDetails.cron;
      const cronComponents = currentCron.split(' ');
      cronComponents[0] = String(parseInt(cronComponents[0], 10) + minutesToAdd);
      cronComponents[1] = String(parseInt(cronComponents[1], 10) + hoursToAdd);
      const newCron = cronComponents.join(' ');
      console.log(`beemTools: ${id} | ${name} | ${currentCron} | ${newCron}`);
      if (confirmed) {
        const dataset = await Dataset.getDatasetById(id);
        const sqlQuery = await dataset.getSavedCode();
        await dataset.deploy({
          sqlQuery,
          trigger,
          triggerDetails: { ...triggerDetails, cron: newCron },
        });
        console.log(`beemTools: new cron is deployed`);
      }
    }
  }
}

export async function addUserToWorkspace(email: string, workspaceId: string): Promise<void> {
  await checkAllowedSuperAdminUser();
  const workspace = await Workspace.getWorkspaceById(workspaceId);
  const rs = await graphql('listUsersByEmail', { email });
  if (rs.items.length === 0) {
    await User.createIfNotExist(email, workspace);
  } else {
    await workspace.addMembers([rs.items[0].id]);
  }
}

function calculateMedian(arr: number[]): number {
  const sortedArr = [...arr].sort((a, b) => a - b);
  const length = sortedArr.length;
  const mid = Math.floor(length / 2);

  if (length % 2 === 0) {
    return (sortedArr[mid - 1] + sortedArr[mid]) / 2;
  } else {
    return sortedArr[mid];
  }
}

const timer = [0, 0];

export function endTimer(): void {
  timer[1] = Date.now();
}

const performanceTestConfig: {
  datasetIds: string[];
  mode: 'WAIT' | 'NO_WAIT' | 'PGCLIENT';
  recheckSeconds: number;
} = {
  datasetIds: [],
  mode: 'NO_WAIT',
  recheckSeconds: 60,
};

export function getPerformanceTestConfigMode(): 'WAIT' | 'NO_WAIT' | 'PGCLIENT' {
  return performanceTestConfig.mode;
}

export function setPerformanceTestConfigMode(m: 'WAIT' | 'NO_WAIT' | 'PGCLIENT'): void {
  performanceTestConfig.mode = m;
}

export async function waitUsingPerformanceTestConfigMode(
  i: number,
  START_WAIT_TIME: number,
): Promise<void> {
  if (performanceTestConfig.mode === 'WAIT') {
    const waitTime = 1000 * Math.min(30, i);
    await new Promise((resolve) => setTimeout(resolve, waitTime));
  } else {
    if (Date.now() > START_WAIT_TIME) {
      await new Promise((resolve) => setTimeout(resolve, 30000));
    }
  }
}

export function enablePerformanceTest(
  arr: string[],
  mode: 'WAIT' | 'NO_WAIT' | 'PGCLIENT',
  recheckSeconds: number,
): void {
  performanceTestConfig.datasetIds.push(...arr);
  performanceTestConfig.mode = mode;
  performanceTestConfig.recheckSeconds = recheckSeconds;
}

async function once_runPerformanceTest(current: any, dispatch: any): Promise<number> {
  timer[0] = Date.now();
  timer[1] = 0;
  dispatch({
    type: 'dataset/getContent',
    payload: { current, pageSize: 200, currentPage: 1 },
  });
  while (!timer[1]) {
    await new Promise((resolve) =>
      setTimeout(resolve, performanceTestConfig.recheckSeconds * 1000),
    );
  }
  const duration = timer[1] - timer[0];
  console.log('timing duration', duration);
  return duration;
}

export async function runPerformanceTest(dispatch: any): Promise<void> {
  const { datasetIds } = performanceTestConfig;
  let datasetId = datasetIds.pop();
  while (datasetId) {
    console.log('timing datasetId', datasetId);
    const current = await Dataset.getDatasetById(datasetId);

    const durations: number[] = [];
    for (let i = 0; i < 10; i++) {
      durations.push(await once_runPerformanceTest(current, dispatch));
    }

    console.log('timing median', calculateMedian(durations));
    datasetId = datasetIds.pop();
  }
}

// COMMENTED OUT SINCE 20250822
// export async function migrationDatasetSizeValues(dryrun = true) {
//   await checkAllowedSuperAdminUser();

//   // get list of dataset without size values
//   let nextToken: string | undefined = undefined;
//   do {
//     const rs: {
//       items: Array<{
//         id: string;
//         totalSize?: number;
//         cloudResourceId: string;
//       }>;
//       nextToken?: string;
//     } = await graphql('listDatasets', { filter: { isDraft: { ne: true } }, nextToken }, true);

//     const items = rs.items.filter((el) => !el.totalSize || el.totalSize === 1);

//     for (let i = 0; i < items.length; i++) {
//       const { id: datasetId, cloudResourceId } = items[i];

//       try {
//         // for each, call the agent to get the size
//         const { totalSize, latestVersionSize } = await graphql('beemAgentMigrationOne', {
//           query: { cloudResourceId },
//         });

//         console.log(
//           `datasetId: ${datasetId} | totalSize: ${totalSize} | latestVersionSize: ${latestVersionSize}`,
//         );

//         // update the dataset with the size
//         if (!dryrun) {
//           await graphql('updateDataset', {
//             input: {
//               id: datasetId,
//               totalSize,
//               latestVersionSize,
//             },
//           });
//           console.log(`datasetId: ${datasetId} | new values set`);
//         }
//       } catch (e) {
//         console.log(`datasetId: ${datasetId} | FAILED TO MIGRATE`);
//       }
//     }

//     nextToken = rs.nextToken;
//   } while (nextToken);
// }

// COMMENTED OUT SINCE 20250824
// export async function migrationDatasetParents(dryrun = true) {
//   await checkAllowedSuperAdminUser();

//   // get list of all datasets workspace by workspace to get full info
//   let totalProcessed = 0;
//   let totalUpdated = 0;

//   // Get current user's organization ID from Redux state
//   const reduxState = getDvaApp()._store.getState().organization;
//   const { currentOrganization } = reduxState;
//   assertIsString(currentOrganization, 'BEEM250603172001');

//   // Get all workspaces first
//   const workspaces = await graphql('listWorkspacesByOrganization', {
//     organizationId: currentOrganization,
//   });

//   for (const workspace of workspaces.items) {
//     console.log(`Processing workspace: ${workspace.name} (${workspace.id})`);

//     let nextToken: string | undefined = undefined;
//     do {
//       const rs: {
//         items: Array<{
//           id: string;
//           name: string;
//           isDraft: boolean;
//         }>;
//         nextToken?: string;
//       } = await graphql('listDatasetsByWorkspace', {
//         workspaceId: workspace.id,
//         nextToken,
//       });

//       const nonDraftItems = rs.items.filter((el) => !el.isDraft);

//       for (let i = 0; i < nonDraftItems.length; i++) {
//         const { id: datasetId, name } = nonDraftItems[i];
//         totalProcessed++;

//         console.log(`[${totalProcessed}] Processing dataset: ${datasetId} | ${name}`);

//         try {
//           // Get the deployed SQL query for this dataset
//           const dataset = await Dataset.getDatasetById(datasetId);
//           const sqlQuery = await dataset.getDeployedCode();

//           if (!sqlQuery) {
//             console.log(`  Skipping - no deployed SQL query found`);
//             continue;
//           }

//           // Parse the query for parent datasets (same logic as in deploy method)
//           const { BeemSqlParser } = await import('./beemSqlParser');
//           const beemSqlParser = new BeemSqlParser({ dbEngine: 'Redshift' });
//           beemSqlParser.parse(sqlQuery);
//           const tables = beemSqlParser.getTables();

//           if (tables.length === 0) {
//             console.log(`  No table references found in SQL query`);
//             continue;
//           }

//           // map the tables to their datasetId
//           const tablesWithId = await Promise.all(
//             tables.map(async (el) => {
//               const { schema, table } = el;
//               const temp2 = schema.split('_')[0].slice(2).match(/^\d+/);
//               if (!temp2) {
//                 console.log(`    Warning: Could not parse sequence number from schema: ${schema}`);
//                 return { schema, table, refType: 'unknown', datasetId: '' };
//               }

//               const seqNumber = parseInt(temp2[0], 10);
//               const refType = schema.split('_')[1];

//               // get workspace from seqNumber and orgId
//               const workspaceFilter = {
//                 seq: { eq: seqNumber },
//               };
//               const workspaces = await graphql('listWorkspacesByOrganization', {
//                 organizationId: currentOrganization,
//                 filter: workspaceFilter,
//               });

//               // get datasetId from workspaceId and table name
//               const workspaceId = workspaces.items[0]?.id;

//               let datasetId = '';
//               switch (refType) {
//                 case 'hub': {
//                   const data = await graphql('listDatasetsByViewName', {
//                     viewName: table,
//                     filter: {
//                       workspaceId: { eq: workspaceId },
//                     },
//                   });
//                   datasetId = data?.items[0]?.id || '';
//                   break;
//                 }
//                 case 'catalog': {
//                   const data = await graphql('listDataObjectsByViewName', {
//                     viewName: table,
//                     filter: {
//                       workspaceId: { eq: workspaceId },
//                     },
//                   });
//                   datasetId = data?.items[0]?.id || '';
//                   break;
//                 }

//                 default:
//                   console.log(`    Warning: Unknown refType: ${refType}`);
//               }
//               return { schema, table, refType, datasetId };
//             }),
//           );

//           const parentsData = {
//             tables: tablesWithId,
//             errors: beemSqlParser.getErrors(),
//           };

//           console.log(`  Parent data: ${JSON.stringify(parentsData)}`);

//           // update the dataset with parent information
//           if (!dryrun) {
//             await graphql('updateDataset', {
//               input: {
//                 id: datasetId,
//                 parents: JSON.stringify(parentsData),
//               },
//             });
//             totalUpdated++;
//           }
//         } catch (error) {
//           const errorMessage = error instanceof Error ? error.message : String(error);
//           console.error(`  Error processing dataset ${datasetId}: ${errorMessage}`);
//         }
//       }

//       nextToken = rs.nextToken;
//     } while (nextToken);
//   }

//   console.log(`\nMigration complete:`);
//   console.log(`  Total datasets processed: ${totalProcessed}`);
//   console.log(`  Total datasets updated: ${totalUpdated}`);
//   console.log(`  Dry run mode: ${dryrun}`);
// }
